import { VStack } from '@chakra-ui/react'
import { NodeProps } from '@xyflow/react'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { TransferNodeData } from '../../converters/shared/reactFlowTypes'
import { useSwitchboardT } from '../../types'
import { text } from '../../utils/text'
import { ExtensionSelector } from '../core'
import { BaseNode, nodePropsAreEqual } from './BaseNode'
import { useTransferNode } from './hooks/useTransferNode'

export interface TransferNodeProps extends NodeProps {
  extensionMap?: Map<number, ExtensionInstance>
  onDelete?: () => void
  onDeleteNode?: (nodeId: string) => void
  showDeleteButton?: boolean
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Transfer node component for call flow diagrams.
 * Allows transferring a call to an extension with an optional timeout.
 */
const TransferNodeComponent: React.FC<TransferNodeProps> = ({
  id,
  data,
  extensionMap: _extensionMap, // Reserved for future use (e.g., display formatting)
  onDelete,
  onDeleteNode,
  showDeleteButton,
}) => {
  const nodeData = data as TransferNodeData & Record<string, unknown>
  const { currentExtensionId, handleExtensionChange, hasData } =
    useTransferNode(id, nodeData)
  const t = useSwitchboardT()

  return (
    <BaseNode
      label={text(t, 'switchboard.nodeLabels.transfer', 'Transfer')}
      showPlaceholder={!hasData}
      onDelete={onDelete}
      onDeleteNode={onDeleteNode}
      id={id}
      showDeleteButton={showDeleteButton}
      sourceHandlePosition={null}
    >
      <VStack align="stretch" gap={2}>
        <ExtensionSelector
          value={currentExtensionId}
          onChange={handleExtensionChange}
          filterMode="direct_line"
        />
      </VStack>
    </BaseNode>
  )
}

export const TransferNode = React.memo(TransferNodeComponent, nodePropsAreEqual)
